// lib/demoProblemBankData.ts

import { ProblemBankEntry, ExternalSubmission } from '@/types/problemBank';

// ==============================================
// PROBLEMS ADDED BY ANALYSTS
// These are fully verified problems ready for squads
// ==============================================

export const ANALYST_ADDED_PROBLEMS: any[] = [
  {
    problem_id: 'VV-RTL-001',
    date_discovered: '2026-02-10',
    source: 'audit',
    title: 'Supermarket Checkout Queue Crisis - Naivas',
    category: ['downtime', 'labor_efficiency', 'logistics'],
    industry_sector: 'fmcg',
    status: 'verified',
    anchor: {
      name: 'Naivas Supermarket - Galleria Mall',
      registrationNumber: 'NAIV/2010/4567',
      yearEstablished: 1990,
      location: 'Nairobi, Galleria Mall',
      plantDepartment: 'Checkout Operations',
      contactPerson: {
        name: 'James Mwangi',
        title: 'Store Manager',
        email: 'jmwangi@naivas.co.ke',
        phone: '+254722445566',
        isDecisionMaker: true,
        alternateContact: {
          name: 'Lucy Wanjiru',
          phone: '+254733778899',
          email: 'lwanjiru@naivas.co.ke'
        }
      },
      relationshipStrength: 4,
      lastContactDate: '2026-03-01',
      nextFollowUp: '2026-03-15'
    },
    quantification: {
      description: `Naivas Galleria experiences severe queue congestion during peak hours (5pm-8pm weekdays, all day weekends). 
      
Key observations from analyst site visit (Feb 15, 2026):
- Average wait time: 18-25 minutes during peak hours
- All 30 trolleys in use during peak times, customers wait for trolleys
- Checkout process stalls 8-12 times daily due to payment issues and price checks
- Customers with small purchases (2-3 items) wait behind full-trolley shoppers
- Estimated 15% abandonment rate during peak hours
- Staff morale affected by constant customer complaints

The system works on paper but breaks down under real-world load.`,
      totalLossPerYear: 2358720000,
      calculationMethodology: `LOSS CALCULATION METHODOLOGY - VERIFIED BY ANALYST:

1. Revenue Loss from Inefficiency:
   - Theoretical capacity: 500 customers/hour
   - Actual throughput during peak: 280 customers/hour
   - Lost capacity: 220 customers/hour
   - Peak inefficiency hours: 140 hours/month
   - Average transaction: KES 2,800
   - Revenue loss: 220 × 140 × 2,800 = KES 86,240,000/month

2. Abandoned Shopping:
   - 15% abandonment rate during peak
   - 75,000 customers/month × 15% = 11,250 abandoned
   - Average abandoned basket: KES 1,500
   - Loss: 11,250 × 1,500 = KES 16,875,000/month

3. Trolley Shortage Impact:
   - 30 trolleys × 3 cycles/hour = 90 customers/hour capacity
   - Demand: 140 customers/hour
   - Lost customers: 50/hour × 140 peak hours = 7,000 customers
   - Loss: 7,000 × 2,800 = KES 19,600,000/month

4. Labor Inefficiency:
   - 5 extra cashiers during peak × 8 hours × 30 days = 1,200 hours
   - Hourly wage: KES 500
   - Labor loss: 1,200 × 500 = KES 600,000/month

5. Customer Churn Impact:
   - 8% annual customer churn due to poor experience
   - Average customer LTV: KES 180,000/year
   - Annual loss: 0.08 × 120,000 customers × 180,000 = KES 1,728,000,000/year

TOTAL ANNUAL LOSS: KES 2.35 BILLION`,
      dataSources: [
        'Analyst site visit observation (Feb 15, 2026)',
        'Checkout transaction logs (Jan-Feb 2026)',
        'Customer exit interviews (50 conducted by analyst)',
        'Staff interviews (12 cashiers)',
        'Security camera footage analysis (3 days)',
        'Trolley utilization tracking (2 weeks)'
      ],
      confidenceLevel: 'high'
    },
    root_cause_analysis: {
      rootCauseCategory: 'Process/Procedure & Technology',
      rootCauseDetails: `Analyst root cause analysis (Feb 16, 2026):

The fundamental issue is single-threaded bottleneck with no parallel processing options.

Primary root causes identified:
1. All customers funnel through same 15 cashiers with no self-service alternative
2. No dynamic lane allocation based on queue length
3. Manual price checks (18-25 per hour) create unpredictable delays
4. M-PESA payment gateway crashes 3-4 times weekly
5. No real-time queue data for customer decisions or staff allocation
6. Trolley shortage compounds the problem`,
      fiveWhys: [
        { level: 1, question: 'Why are queues so long?', answer: 'Checkout cannot handle peak volume' },
        { level: 2, question: 'Why can\'t it handle peak volume?', answer: 'All customers go through same 15 cashiers' },
        { level: 3, question: 'Why no self-service?', answer: 'Management fears theft' },
        { level: 4, question: 'Why is theft concern blocking innovation?', answer: 'Previous attempt at basic self-checkout had 12% shrinkage' },
        { level: 5, question: 'Why did that solution fail?', answer: 'No theft prevention integration - pure trust-based system' }
      ],
      barriersToInternalSolution: [
        'No in-house software development team',
        'Previous failed attempt with basic scanning app (high theft)',
        'Budget allocated for store expansion, not digital transformation',
        'Vendor lock-in with current POS provider (3-year contract)',
        'No clear ROI model for self-service investment'
      ]
    },
    solution_potential: {
      hypothesis: `Analyst-proposed solution: SmartScan - AI-powered self-scanning with theft prevention

Core solution: Mobile app + in-store verification system
- Customers scan items as they shop using phone camera
- Virtual trolley updates in real-time
- QR code generated for express payment
- Random spot-check verification (5% of customers)
- AI-powered anomaly detection flags suspicious patterns
- Integration with loyalty program and personalized offers`,
      solutionType: 'software',
      keyFeatures: [
        'Mobile app with barcode scanning',
        'Real-time trolley total',
        'QR code express checkout',
        'Random spot-check system',
        'Staff monitoring dashboard',
        'AI anomaly detection',
        'Customer trust scoring',
        'Loyalty integration',
        'Traffic analytics',
        'Inventory tracking',
        'M-PESA integration'
      ],
      mvpFeatures: [
        'Basic barcode scanning',
        'QR payment code generation',
        'Simple staff verification app',
        'Transaction history',
        'Manual override for cashiers'
      ],
      estimatedDevCost: 3500000,
      estimatedDevTimeline: 10,
      estimatedDeployCost: 120000,
      estimatedPriceModel: 'KES 45,000/month per store + 0.3% transaction fee OR KES 450,000 one-time license + KES 25,000/month support',
      anchorPaybackMonths: 3,
      marketSizeKenya: 450,
      marketSizeEastAfrica: 1800,
      competitorLandscape: `EXISTING SOLUTIONS IN KENYAN MARKET:
1. Scan & Go (international) - $60K/year, no theft prevention
2. Traditional self-checkout kiosks - KES 1.2M per unit, 8-12% theft
3. Basic barcode apps - free, 15-20% fraud rate
4. South African ScanPay - not available in Kenya

GAP IN MARKET:
No solution combines self-scanning with intelligent theft prevention, M-PESA integration, and real-time analytics at an affordable price point for Kenyan supermarkets.`,
      scalabilityScore: 5
    },
    verification: {
      observedFirsthand: true,
      observationDate: '2026-02-15',
      observerName: 'Wycliffe Kirwa',
      observationMethod: 'onsite',
      photoVideoEvidence: [
        'https://drive.google.com/naivas-queue-1.jpg',
        'https://drive.google.com/naivas-queue-2.jpg',
        'https://drive.google.com/naivas-queue-video.mp4',
        'https://drive.google.com/naivas-trolley-shortage.jpg',
        'https://drive.google.com/naivas-staff-interview.mp4'
      ],
      anchorAcknowledgment: {
        obtained: true,
        method: 'email',
        proofUrl: 'https://drive.google.com/naivas-acknowledgment.pdf',
        date: '2026-02-20'
      },
      anchorUrgency: 5,
      willingToPay: 'yes',
      loiPotential: 5
    },
    priority_score: {
      lossMagnitude: { score: 5, annualLoss: 2358720000 },
      anchorUrgency: { score: 5, description: 'Critical - losing customers daily, peak Easter season approaching' },
      solutionFeasibility: { score: 4, description: 'Likely - technology exists, needs integration with M-PESA' },
      marketSize: { score: 5, customerCount: 450 },
      relationshipStrength: { score: 4, description: 'Trusted - open communication, sharing sensitive data' },
      totalScore: 4.8,
      priorityTier: 'critical',
      calculatedAt: '2026-02-25',
      calculatedBy: 'Wycliffe Kirwa'
    },
    created_at: new Date('2026-02-10T10:30:00Z').toISOString(),
    updated_at: new Date('2026-03-01T14:20:00Z').toISOString()
  },
  {
    problem_id: 'VV-AGRO-002',
    date_discovered: '2026-02-18',
    source: 'audit',
    title: 'Tea Drying Energy Overconsumption - Kericho',
    category: ['energy_waste', 'maintenance'],
    industry_sector: 'agro_processing',
    status: 'verified',
    anchor: {
      name: 'Kericho Tea Estates',
      registrationNumber: 'KTE/1985/1234',
      yearEstablished: 1985,
      location: 'Kericho',
      plantDepartment: 'Processing Plant',
      contactPerson: {
        name: 'Samuel Kiprono',
        title: 'Operations Director',
        email: 'samuel@kerichotea.com',
        phone: '+254733456789',
        isDecisionMaker: true
      },
      relationshipStrength: 4,
      lastContactDate: '2026-03-02',
      nextFollowUp: '2026-03-16'
    },
    quantification: {
      description: 'Tea drying process consumes 35% more energy than industry benchmark due to outdated temperature control systems.',
      totalLossPerYear: 13728000,
      calculationMethodology: 'Compared energy bills (Jan-Dec 2025) against production volume. Identified 35% excess consumption during drying cycles.',
      dataSources: ['Utility bills 2025', 'Production logs', 'Equipment spec sheets'],
      confidenceLevel: 'high'
    },
    root_cause_analysis: {
      rootCauseCategory: 'Process/Procedure',
      rootCauseDetails: 'Drying temperatures set 15°C higher than necessary due to outdated spec sheets from 2015; no real-time moisture monitoring',
      fiveWhys: [
        { level: 1, question: 'Why is energy consumption high?', answer: 'Dryers run at 120°C instead of 105°C' },
        { level: 2, question: 'Why are temperatures too high?', answer: 'Following 10-year-old spec sheets' },
        { level: 3, question: 'Why are old specs still used?', answer: 'No process review in 5 years' },
        { level: 4, question: 'Why no process review?', answer: 'No dedicated continuous improvement team' },
        { level: 5, question: 'Why no CI team?', answer: 'Seen as overhead, not investment' }
      ],
      barriersToInternalSolution: ['No in-house process engineering expertise', 'Resistance to change from long-time staff']
    },
    solution_potential: {
      hypothesis: 'IoT-enabled smart temperature controller with real-time optimization and moisture sensors',
      solutionType: 'hybrid',
      keyFeatures: ['Automated temperature control', 'Moisture sensors', 'Energy consumption analytics', 'Remote monitoring'],
      mvpFeatures: ['Basic controller', 'Temperature logging', 'Manual override'],
      estimatedDevCost: 280000,
      estimatedDevTimeline: 8,
      estimatedDeployCost: 180000,
      estimatedPriceModel: 'KES 280,000 installation + KES 120,000/year subscription',
      anchorPaybackMonths: 5,
      marketSizeKenya: 180,
      marketSizeEastAfrica: 540,
      competitorLandscape: 'International players (Siemens, ABB) cost 3-5x more. No local solution.',
      scalabilityScore: 5
    },
    verification: {
      observedFirsthand: true,
      observationDate: '2026-02-20',
      observerName: 'Analyst Demo',
      observationMethod: 'onsite',
      photoVideoEvidence: ['https://drive.google.com/kericho-dryers.jpg', 'https://drive.google.com/kericho-control-panel.jpg'],
      anchorAcknowledgment: {
        obtained: true,
        method: 'email',
        proofUrl: 'https://drive.google.com/kericho-acknowledgment.pdf',
        date: '2026-02-25'
      },
      anchorUrgency: 4,
      willingToPay: 'yes',
      loiPotential: 4
    },
    priority_score: {
      lossMagnitude: { score: 4, annualLoss: 13728000 },
      anchorUrgency: { score: 4, description: 'High - wants solution within 6 months' },
      solutionFeasibility: { score: 4, description: 'Likely - existing tech can be adapted' },
      marketSize: { score: 5, customerCount: 180 },
      relationshipStrength: { score: 4, description: 'Trusted - multiple meetings' },
      totalScore: 4.2,
      priorityTier: 'critical',
      calculatedAt: '2026-02-28',
      calculatedBy: 'Analyst Demo'
    },
    created_at: new Date('2026-02-18T09:15:00Z').toISOString(),
    updated_at: new Date('2026-03-02T11:30:00Z').toISOString()
  },
  {
    problem_id: 'VV-LOG-003',
    date_discovered: '2026-02-22',
    source: 'audit',
    title: 'Cold Chain Temperature Breaches - Brookside',
    category: ['logistics', 'quality_control'],
    industry_sector: 'logistics',
    status: 'verified',
    anchor: {
      name: 'Brookside Dairy',
      registrationNumber: 'BSD/1998/5678',
      yearEstablished: 1998,
      location: 'Nairobi',
      plantDepartment: 'Distribution',
      contactPerson: {
        name: 'Mary Akinyi',
        title: 'Quality Assurance Manager',
        email: 'mary@brooksidedairy.com',
        phone: '+254744567890',
        isDecisionMaker: true
      },
      relationshipStrength: 3,
      lastContactDate: '2026-03-03',
      nextFollowUp: '2026-03-10'
    },
    quantification: {
      description: 'Temperature fluctuations during transport of fresh milk products causing spoilage. Analyst reviewed temperature logger data from 50 deliveries.',
      totalLossPerYear: 25560000,
      calculationMethodology: 'Spoilage records show 7% product loss on long-distance routes. Labor costs for quality checks and rework included.',
      dataSources: ['Quality control reports', 'Temperature logger data (50 deliveries)', 'Delivery manifests'],
      confidenceLevel: 'high'
    },
    root_cause_analysis: {
      rootCauseCategory: 'Equipment/Machinery',
      rootCauseDetails: 'Refrigeration units on trucks lack real-time monitoring; drivers only check temperature at delivery points',
      fiveWhys: [
        { level: 1, question: 'Why is milk spoiling?', answer: 'Temperature exceeds 4°C during transport' },
        { level: 2, question: 'Why does temperature exceed limits?', answer: 'Refrigeration units malfunction undetected' },
        { level: 3, question: 'Why are malfunctions undetected?', answer: 'No real-time monitoring system' },
        { level: 4, question: 'Why no monitoring system?', answer: 'Cost considered prohibitive for 50 trucks' },
        { level: 5, question: 'Why considered cost prohibitive?', answer: 'Only evaluated expensive enterprise IoT solutions' }
      ],
      barriersToInternalSolution: ['Budget constraints', 'Lack of IoT expertise']
    },
    solution_potential: {
      hypothesis: 'Low-cost IoT temperature monitors with cellular connectivity and real-time alerts',
      solutionType: 'hardware',
      keyFeatures: ['Real-time temperature monitoring', 'SMS alerts to drivers', 'Dashboard for fleet managers', 'Historical data logging'],
      mvpFeatures: ['Temperature sensors', 'Cellular connectivity', 'Basic SMS alerts'],
      estimatedDevCost: 220000,
      estimatedDevTimeline: 6,
      estimatedDeployCost: 45000,
      estimatedPriceModel: 'KES 22,000 per truck + KES 4,000/month monitoring fee',
      anchorPaybackMonths: 3,
      marketSizeKenya: 650,
      marketSizeEastAfrica: 2000,
      competitorLandscape: 'Enterprise IoT solutions cost 5-8x more. No affordable option for medium fleets.',
      scalabilityScore: 5
    },
    verification: {
      observedFirsthand: true,
      observationDate: '2026-02-24',
      observerName: 'Analyst Demo',
      observationMethod: 'onsite',
      photoVideoEvidence: ['https://drive.google.com/brookside-truck.jpg', 'https://drive.google.com/brookside-logger-data.jpg'],
      anchorAcknowledgment: {
        obtained: true,
        method: 'email',
        proofUrl: 'https://drive.google.com/brookside-acknowledgment.pdf',
        date: '2026-02-28'
      },
      anchorUrgency: 3,
      willingToPay: 'yes',
      loiPotential: 4
    },
    priority_score: {
      lossMagnitude: { score: 4, annualLoss: 25560000 },
      anchorUrgency: { score: 3, description: 'Medium - would like within year' },
      solutionFeasibility: { score: 4, description: 'Likely - off-the-shelf components' },
      marketSize: { score: 5, customerCount: 650 },
      relationshipStrength: { score: 3, description: 'Engaged - sharing data' },
      totalScore: 3.9,
      priorityTier: 'high',
      calculatedAt: '2026-03-01',
      calculatedBy: 'Analyst Demo'
    },
    created_at: new Date('2026-02-22T14:20:00Z').toISOString(),
    updated_at: new Date('2026-03-03T09:45:00Z').toISOString()
  }
];

// ==============================================
// EXTERNAL SUBMISSIONS FROM COMPANIES
// These are pending or under review by analysts
// ==============================================

export const EXTERNAL_SUBMISSIONS: any[] = [
  {
    company_name: 'QuickMart Supermarkets',
    company_registration: 'QMK/2015/8901',
    contact_name: 'Sarah Chebet',
    contact_title: 'Regional Manager',
    contact_email: 'schebet@quickmart.co.ke',
    contact_phone: '+254722334455',
    problem_title: 'Express Lane Abuse and Queue Management',
    problem_description: 'Our express lanes (10 items or less) are constantly clogged with customers having 20-30 items. Cashiers cannot enforce limits without conflict. During peak hours, all 8 express lanes are effectively regular lanes, defeating their purpose. Small-purchase customers are abandoning carts.',
    estimated_impact: 'KES 18-25M per year',
    urgency: 'high',
    documents: [
      'https://drive.google.com/quickmart-queue-study.pdf',
      'https://drive.google.com/quickmart-customer-survey.xlsx',
      'https://drive.google.com/quickmart-lane-utilization.csv'
    ],
    status: 'pending',
    submitted_at: new Date('2026-03-05T09:30:00Z').toISOString()
  },
  {
    company_name: 'Tuskys - Kahawa West',
    company_registration: 'TUS/2012/3456',
    contact_name: 'Peter Kimani',
    contact_title: 'Branch Manager',
    contact_email: 'pkimani@tuskys.co.ke',
    contact_phone: '+254744556677',
    problem_title: 'M-PESA Payment Gateway Downtime',
    problem_description: 'M-PESA payment system crashes 4-5 times weekly during peak hours (5-8pm). Each outage lasts 15-30 minutes. During this time, customers abandon purchases or leave without paying for already-bagged items. Staff have to manually process payments when system returns, creating more backlog.',
    estimated_impact: 'KES 8-12M per year',
    urgency: 'high',
    documents: [
      'https://drive.google.com/tuskys-mpesa-logs.xlsx',
      'https://drive.google.com/tuskys-abandonment-study.pdf'
    ],
    status: 'under_review',
    submitted_at: new Date('2026-03-03T16:15:00Z').toISOString(),
    reviewed_by: 'analyst-001',
    reviewed_at: new Date('2026-03-04T10:30:00Z').toISOString(),
    review_notes: 'Interesting problem. May be related to payment provider rather than store systems. Requesting M-PESA API logs.'
  },
  {
    company_name: 'Chandarana Foodplus',
    company_registration: 'CHAN/2017/2345',
    contact_name: 'Rita Shah',
    contact_title: 'Operations Director',
    contact_email: 'rshah@chandarana.co.ke',
    contact_phone: '+254722998877',
    problem_title: 'Queue Abandonment Tracking and Prevention',
    problem_description: 'Our data shows 18% of customers abandon carts when queues exceed 10 minutes. Weekend losses are severe (25% abandonment). We have cameras but no way to predict queue buildup or alert staff. Need a system that can: 1) Predict queue lengths 30min in advance, 2) Alert managers to open lanes, 3) Redirect customers to faster lanes.',
    estimated_impact: 'KES 45-60M per year',
    urgency: 'high',
    documents: [
      'https://drive.google.com/chandarana-abandonment-data.xlsx',
      'https://drive.google.com/chandarana-camera-footage.mp4',
      'https://drive.google.com/chandarana-traffic-patterns.pdf'
    ],
    status: 'pending',
    submitted_at: new Date('2026-03-06T11:20:00Z').toISOString()
  },
  {
    company_name: 'Naivas - Thika Road',
    company_registration: 'NAIV/2010/7890',
    contact_name: 'John Mbugua',
    contact_title: 'Store Manager',
    contact_email: 'jmbugua@naivas.co.ke',
    contact_phone: '+254733445566',
    problem_title: 'Trolley Theft and Tracking',
    problem_description: 'We lose 25-30 shopping trolleys per month to theft. Trolleys are taken to nearby estates and never returned. Replacement cost is KES 8,500 each. We need a way to: 1) Track trolley locations, 2) Alert when trolleys leave store perimeter, 3) Deter theft with alarms.',
    estimated_impact: 'KES 2.5-3M per year',
    urgency: 'medium',
    documents: [
      'https://drive.google.com/naivas-trolley-loss.xlsx',
      'https://drive.google.com/naivas-theft-photos.jpg'
    ],
    status: 'pending',
    submitted_at: new Date('2026-03-04T13:45:00Z').toISOString()
  },
  {
    company_name: 'Sasini Tea',
    company_registration: 'SAS/1960/4567',
    contact_name: 'David Maina',
    contact_title: 'Plant Engineer',
    contact_email: 'dmaina@sasini.co.ke',
    contact_phone: '+254722112233',
    problem_title: 'Tea Dryer Temperature Inefficiency',
    problem_description: 'Our tea dryers consume 30% more energy than 5 years ago. Maintenance says equipment is fine, but fuel bills keep rising. We suspect inefficient combustion or heat loss but don\'t have monitoring equipment to diagnose.',
    estimated_impact: 'KES 15-20M per year',
    urgency: 'medium',
    documents: [
      'https://drive.google.com/sasini-fuel-bills-2025.pdf',
      'https://drive.google.com/sasini-dryer-specs.pdf'
    ],
    status: 'pending',
    submitted_at: new Date('2026-03-02T10:00:00Z').toISOString()
  }
];

// ==============================================
// MASTER DEMO DATA OBJECT
// ==============================================

export const DEMO_PROBLEM_BANK_DATA = {
  analystProblems: ANALYST_ADDED_PROBLEMS,
  externalSubmissions: EXTERNAL_SUBMISSIONS,
  summary: {
    totalAnalystProblems: ANALYST_ADDED_PROBLEMS.length,
    totalExternalSubmissions: EXTERNAL_SUBMISSIONS.length,
    totalVerifiedLoss: ANALYST_ADDED_PROBLEMS.reduce((sum, p) => sum + (p.quantification?.totalLossPerYear || 0), 0),
    criticalProblems: ANALYST_ADDED_PROBLEMS.filter(p => p.priority_score?.priorityTier === 'critical').length,
    highPriorityProblems: ANALYST_ADDED_PROBLEMS.filter(p => p.priority_score?.priorityTier === 'high').length
  }
};